import { inject, InjectionToken, Service } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { dayjs, Dayjs } from '@app/shared/dayjs-setup';

export interface DayjsDateAdapterOptions {
  useUtc: boolean;
}

export const MAT_DAYJS_DATE_ADAPTER_OPTIONS = new InjectionToken<DayjsDateAdapterOptions>(
  'MAT_DAYJS_DATE_ADAPTER_OPTIONS',
);

@Service({ autoProvided: false })
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  private readonly _useUtc = inject(MAT_DAYJS_DATE_ADAPTER_OPTIONS, { optional: true })?.useUtc ?? false;

  constructor() {
    super();
    const dateLocale = inject<string>(MAT_DATE_LOCALE, { optional: true });
    this.setLocale(dateLocale || dayjs.locale());
  }

  override setLocale(locale: string): void {
    super.setLocale(locale);
  }

  getYear(date: Dayjs): number {
    return date.year();
  }

  getMonth(date: Dayjs): number {
    return date.month();
  }

  getDate(date: Dayjs): number {
    return date.date();
  }

  getDayOfWeek(date: Dayjs): number {
    return date.day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const dtf = new Intl.DateTimeFormat(this.locale, { month: style, timeZone: 'utc' });
    return Array.from({ length: 12 }, (_, i) => dtf.format(new Date(Date.UTC(2017, i, 1))));
  }

  getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const dtf = new Intl.DateTimeFormat(this.locale, { weekday: style, timeZone: 'utc' });
    // Jan 1 2017 was a Sunday
    return Array.from({ length: 7 }, (_, i) => dtf.format(new Date(Date.UTC(2017, 0, i + 1))));
  }

  getYearName(date: Dayjs): string {
    return String(date.year());
  }

  getFirstDayOfWeek(): number {
    // Monday = 1 for most locales; Sunday = 0 for en-US
    return 1;
  }

  getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    const d = this._createDayjs().year(year).month(month).date(date).startOf('day');
    return d;
  }

  today(): Dayjs {
    return this._createDayjs().startOf('day');
  }

  parse(value: string | number, parseFormat: string | string[]): Dayjs | null {
    if (typeof value === 'string' && value.length > 0) {
      const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
      for (const fmt of formats) {
        const parsed = this._useUtc ? dayjs.utc(value, fmt, true) : dayjs(value, fmt, true);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      // Try ISO fallback
      const iso = this._useUtc ? dayjs.utc(value) : dayjs(value);
      return iso.isValid() ? iso : null;
    }
    if (typeof value === 'number') {
      const d = this._useUtc ? dayjs.utc(value) : dayjs(value);
      return d.isValid() ? d : null;
    }
    return null;
  }

  format(date: Dayjs, displayFormat: string): string {
    return date.format(displayFormat);
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return date.toISOString();
  }

  isDateInstance(obj: unknown): obj is Dayjs {
    return dayjs.isDayjs(obj);
  }

  isValid(date: Dayjs): boolean {
    return date.isValid();
  }

  invalid(): Dayjs {
    return dayjs('invalid-date');
  }

  override deserialize(value: unknown): Dayjs | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      const d = this._useUtc ? dayjs.utc(value) : dayjs(value);
      return d.isValid() ? d : null;
    }
    if (typeof value === 'number') {
      const d = this._useUtc ? dayjs.utc(value) : dayjs(value);
      return d.isValid() ? d : null;
    }
    if (dayjs.isDayjs(value)) {
      return this.clone(value as Dayjs);
    }
    if (value instanceof Date) {
      const d = this._useUtc ? dayjs.utc(value) : dayjs(value);
      return d.isValid() ? d : null;
    }
    return super.deserialize(value);
  }

  private _createDayjs(): Dayjs {
    return this._useUtc ? dayjs.utc() : dayjs();
  }
}

export const DAYJS_DATE_FORMATS = {
  parse: {
    // Primary display format first; numeric fallbacks keep the field forgiving
    // to type into (the adapter tries each format in order, strict mode).
    dateInput: ['DD MMM YY', 'DD.MM.YYYY', 'DD/MM/YYYY'],
  },
  display: {
    dateInput: 'DD MMM YY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
